import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFlag } from 'flags';
import { debounce } from 'lodash';

import Button from 'components/Button/Button';
import Filter from 'components/Filter/Filter';
import Tooltip from 'components/Tooltip/Tooltip';
import Pagination from 'components/Pagination/Pagination';
import DirectionIcon from 'components/DirectionIcon/DirectionIcon';
import DeleteAudience from './Modals/DeleteAudience';
import { defaultModal, openModal, showModal } from 'helpers/modals';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';

import colors from 'theme/Colors.module.scss';
import './Audiences.scss';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  TableContainer,
  Progress,
  Flex,
} from '@chakra-ui/react';

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  useGetAudiencesQuery,
  useDeleteAudienceMutation,
} from 'modules/audiences/audienceApi';

const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

const Audiences = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isReadOnly = useFlag(['features', 'isReadOnly']);
  const [warningModal, setWarningModal] = useState(defaultModal);

  const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);

  // Raw search input (not debounced)
  const [searchInput, setSearchInput] = useState('');

  // Debounced search parameters
  const [defaultSearchParameters, setDefaultSearchParameters] = useState({
    search: '',
    sort: [{ field: 'name', direction: 'asc' }],
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        setDefaultSearchParameters(prev => ({ ...prev, search: query }));
        setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
      }, 500), // Adjust debounce delay
    [] // No dependencies, so it's created only once
  );

  // Wrap it in useCallback so it doesn't change on every render
  const handleDebouncedSearch = useCallback(
    query => {
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const {
    data: audiencesData,
    error,
    isLoading,
    isFetching,
  } = useGetAudiencesQuery({
    ...defaultSearchParameters,
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
    sort: defaultSearchParameters.sort || [],
  });

  const [deleteAudience] = useDeleteAudienceMutation();

  const handleDeleteAud = async id => {
    try {
      await deleteAudience(id).unwrap(); // Call the mutation and unwrap the response

      setWarningModal(defaultModal);

      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Audience removed successfully',
        })
      );
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Failed to remove a Audience',
        })
      );
    }
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Audience Name',
      textAlign: 'center',
      cell: props => <Text>{props.getValue()}</Text>,
    },
    {
      accessorKey: 'inserted_at',
      header: 'Date',
      textAlign: 'center',
      cell: props => (
        <Text>
          {props.getValue()
            ? format(parseISO(props.getValue()), 'LLL do, yyyy h:mm:ss aaa', {
                timeZone: userTz,
              })
            : '--'}
        </Text>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Last Updated',
      textAlign: 'center',
      cell: props => (
        <Text>
          {props.getValue()
            ? format(parseISO(props.getValue()), 'LLL do, yyyy h:mm:ss aaa', {
                timeZone: userTz,
              })
            : '--'}
        </Text>
      ),
    },

    {
      accessorKey: 'is_campaign_using',
      header: 'Remove',
      enableSorting: false,
      textAlign: 'center',
      cell: props => {
        const { id, name } = props.row.original;
        return (
          <Box className="aud-actions" style={{ paddingLeft: '0.5rem' }}>
            <Tooltip
              tipText={
                props.getValue()
                  ? 'Detach audience from all campaigns to remove.'
                  : 'Delete audience.'
              }
              direction="left"
              render={() => (
                <Button
                  icon="delete"
                  iconColor={colors.slate}
                  btnStyle="transparent"
                  disabled={props.getValue() || isReadOnly}
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                    const clickedId = Number(
                      event.currentTarget.getAttribute('data-id')
                    );

                    if (!props.getValue() && clickedId === id) {
                      openModal(
                        setWarningModal,
                        id,
                        'Caution: Audience Removal!',
                        <DeleteAudience
                          callback={handleDeleteAud}
                          name={name}
                          id={id}
                        />
                      );
                    }
                  }}
                  data-id={id}
                />
              )}
            />
          </Box>
        );
      },
    },
  ];

  const table = useReactTable({
    data: audiencesData?.data.entities || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    manualPagination: true, // Enable manual pagination
    state: {
      pagination,
      sorting,
    },
  });

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch Audiences',
      })
    );
  }

  // Handle rows per clicked
  const handleRowClick = row => {
    setSelectedRowId(prev => (prev === row.id ? null : row.id)); // Toggle selection
    navigate(`/audiences/${encodeURIComponent(row.original.id)}`);
  };

  // Handle sort clicked
  const handleSortClick = header => {
    if (header.column.getNextSortingOrder() === 'asc') {
      header.column.toggleSorting(false); // Set to 'asc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'asc',
          },
        ],
      });
    } else if (header.column.getNextSortingOrder() === 'desc') {
      header.column.toggleSorting(true); // Set to 'desc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'desc',
          },
        ],
      });
    } else {
      header.column.clearSorting(); // Remove sorting
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [],
      });
    }
  };

  return (
    <>
      <Box className="aud-header">
        <Box className="aud-header__left">
          {!isReadOnly && (
            <Button
              type="button"
              url="/audiences/new"
              icon="add"
              text="New Audience"
              textColor={colors.slate}
              btnStyle="transparent"
            />
          )}
        </Box>

        <Box className="aud-header__nav"></Box>

        <Box className="aud-header__right">
          <Box className="aud-status">Currently used in campaign.</Box>
        </Box>
      </Box>

      <Box className="aud-body">
        <Box height="60px" className="rail rail-rule">
          <Filter
            value={searchInput}
            placeholder="Search audiences"
            onChange={event => {
              setSearchInput(event); // Update raw input immediately
              handleDebouncedSearch(event); // Trigger debounced update
            }}
          />
        </Box>

        <Box className="aud-table" borderWidth="1px">
          <TableContainer>
            {(isLoading || isFetching) && (
              <Progress size="xs" isIndeterminate />
            )}
            <Table size="md" variant="simple">
              <Thead zIndex={1} bg="white">
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr className="aud-table__row" key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const textAlign =
                        header.column.columnDef.textAlign || 'left';
                      return (
                        <Th
                          className="aud-table__header"
                          key={header.id}
                          whiteSpace="nowrap"
                          textAlign={textAlign}
                        >
                          {header.column.getCanSort() ? (
                            <Flex justify={textAlign} align="center">
                              <Text
                                className="aud-table__header-text"
                                mr={2}
                                onClick={() => handleSortClick(header)}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                              </Text>

                              <Box
                                onClick={() => handleSortClick(header)}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {defaultSearchParameters.sort[0]?.field ===
                                header.id ? (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={
                                      defaultSearchParameters.sort[0]
                                        ?.direction || 'asc'
                                    }
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                ) : (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={'normal'}
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                )}
                              </Box>
                            </Flex>
                          ) : (
                            <Text className="aud-table__header-text" mr={2}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </Text>
                          )}
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map(row => (
                  <Tr
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    bg={row.id === selectedRowId ? 'gray.100' : 'transparent'}
                    color={colors.slate}
                    _hover={{
                      bg: colors.lighterGrey,
                      textColor: colors.deepSlate2,
                    }}
                    cursor="pointer"
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      const { is_campaign_using } = row.original;
                      const textAlign =
                        cell.column.columnDef.textAlign || 'left';
                      return (
                        <Td
                          className="aud-table__body-cell"
                          px={index === 0 ? '0px' : '15px'}
                          py="0px"
                          key={cell.id}
                          textAlign={textAlign}
                        >
                          {index === 0 ? (
                            <Box className="aud-table__first-cell-container">
                              <Box
                                className="aud-table__indicator"
                                bg={`${
                                  is_campaign_using ? 'green.400' : 'gray.400'
                                }`}
                              ></Box>
                              <Box className="aud-table__first-cell">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Box>
                            </Box>
                          ) : (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
        <Pagination
          table={table}
          data={audiencesData}
          pagination={pagination}
          setPagination={setPagination}
          setSelectedRowId={setSelectedRowId}
        />
      </Box>
      {showModal(warningModal, setWarningModal)}
      <Box className="aud-footer"></Box>
    </>
  );
};

export default Audiences;
